// This source code is owned by Avalara Inc. Unauthorized copying of this file,
// via any medium, is strictly prohibited. Proprietary and confidential.

import { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Generic useBlocker hook for React Router v4.3.1
 * Mimics the React Router v6 useBlocker API
 * 
 * This hook allows you to block navigation based on custom conditions.
 * The caller provides a shouldBlock function that determines when to block navigation.
 * This hook is completely generic and can be used for any blocking scenario:
 * - Forms with unsaved changes
 * - Active guided tours
 * - Pending API requests
 * - Any custom condition
 * 
 * @param {Object} history - React Router history object
 * @param {Function} shouldBlock - Function that receives {currentLocation, nextLocation, action} and returns:
 *   - boolean: true to block navigation, false to allow
 *   - Promise<boolean>: async function that resolves to true/false
 *   The function is called synchronously, but can trigger async operations internally.
 *   For async checks, the hook will block first, then you can proceed/reset based on async result.
 * 
 * @returns {Object} blocker object with the following properties:
 *   - state: 'unblocked' | 'blocked' - Current state of the blocker
 *   - location: Object | null - The location that was blocked (if state is 'blocked')
 *   - proceed: Function - Call this to proceed with the blocked navigation
 *   - reset: Function - Call this to cancel the blocked navigation and stay on current page
 * 
 * @example
 * // Block navigation when form has unsaved changes
 * const blocker = useBlocker(
 *   history,
 *   ({ currentLocation, nextLocation }) => {
 *     return hasUnsavedChanges && currentLocation.pathname !== nextLocation.pathname;
 *   }
 * );
 * 
 * @example
 * // Block navigation when tour is active
 * const blocker = useBlocker(
 *   history,
 *   ({ currentLocation, nextLocation }) => {
 *     return isTourActive;
 *   }
 * );
 * 
 * @example
 * // Usage when navigation is blocked
 * if (blocker.state === 'blocked') {
 *   // Show confirmation dialog
 *   if (userConfirms) {
 *     blocker.proceed(); // Navigate to the blocked location
 *   } else {
 *     blocker.reset(); // Cancel navigation and stay on current page
 *   }
 * }
 */
function useBlocker(history, shouldBlock) {
  const [blockerState, setBlockerState] = useState({ state: 'unblocked', location: null });
  const unblockRef = useRef(null);
  const pendingLocationRef = useRef(null);
  const currentLocationRef = useRef(null);
  const shouldBlockRef = useRef(shouldBlock);
  const isBlockedRef = useRef(false); // Track blocking state with ref to avoid race conditions
  const isProcessingRef = useRef(false); // Prevent concurrent proceed/reset calls

  // Keep shouldBlock ref updated
  useEffect(() => {
    shouldBlockRef.current = shouldBlock;
  }, [shouldBlock]);

  // Initialize current location
  useEffect(() => {
    if (history && history.location) {
      currentLocationRef.current = {
        pathname: history.location.pathname,
        search: history.location.search || ''
      };
    }
  }, [history]);

  // Update location when history changes (for non-blocked navigations)
  // This ensures we always have the latest location, but only when not blocked
  // This is critical for handling rapid URL changes (e.g., tour buttons)
  useEffect(() => {
    if (history && history.location && !isBlockedRef.current && !isProcessingRef.current) {
      const newLocation = {
        pathname: history.location.pathname || '',
        search: history.location.search || ''
      };
      // Always update to ensure we have the latest location
      // This handles race conditions where URL changes happen rapidly
      currentLocationRef.current = newLocation;
    }
  }, [history?.location?.pathname, history?.location?.search]);
  
  // Also listen to history changes via history.listen for immediate updates
  // This catches PUSH actions that happen outside the block callback
  useEffect(() => {
    if (!history || !history.listen) return;
    
    const unlisten = history.listen((location) => {
      // Only update if not blocked and not processing
      // This ensures we track location changes immediately, even for rapid changes
      if (!isBlockedRef.current && !isProcessingRef.current && location) {
        currentLocationRef.current = {
          pathname: location.pathname || '',
          search: location.search || ''
        };
      }
    });
    
    return unlisten;
  }, [history]);

  useEffect(() => {
    if (!history || !history.block) return;

    const unblock = history.block((location, action) => {
      // For non-POP actions (PUSH, REPLACE), update location immediately and allow navigation
      // This is critical for handling rapid URL changes (e.g., tour buttons changing URL)
      if (action !== 'POP') {
        // Allow navigation and update current location immediately
        // Use the location parameter which is the target location for PUSH/REPLACE
        if (location.pathname) {
          currentLocationRef.current = {
            pathname: location.pathname || '',
            search: location.search || ''
          };
        }
        return true;
      }

      // For POP actions (back/forward), always use history.location as source of truth
      // The history.block callback is called BEFORE navigation, so history.location is the current location
      // However, we need to account for recent PUSH actions that might not be reflected yet
      // So we use the most recent location we know about
      const actualCurrentLocation = {
        pathname: history.location?.pathname || currentLocationRef.current?.pathname || '',
        search: history.location?.search || currentLocationRef.current?.search || ''
      };

      const nextLocation = {
        pathname: location.pathname || '',
        search: location.search || ''
      };
      
      // Ensure currentLocationRef is in sync with actual current location
      // This handles race conditions where URL was just changed via PUSH
      if (actualCurrentLocation.pathname !== currentLocationRef.current?.pathname ||
          actualCurrentLocation.search !== currentLocationRef.current?.search) {
        currentLocationRef.current = actualCurrentLocation;
      }

      // Check if we should block this navigation using actual current location
      // Caller's shouldBlock function decides whether to block
      // It can be synchronous (returns boolean) or trigger async operations
      const shouldBlockNavigation = shouldBlockRef.current?.({ 
        currentLocation: actualCurrentLocation, 
        nextLocation,
        action // Include action so caller can differentiate POP/PUSH/REPLACE if needed
      });

      if (!shouldBlockNavigation) {
        // Update current location and allow navigation
        currentLocationRef.current = nextLocation;
        isBlockedRef.current = false;
        return true;
      }

      // Block navigation and store pending location
      // Use ref to check state immediately without waiting for state update
      if (!isBlockedRef.current) {
        isBlockedRef.current = true;
        pendingLocationRef.current = nextLocation;
        // Use functional update to ensure we have latest state
        setBlockerState(prevState => {
          // Only update if not already blocked to avoid unnecessary re-renders
          if (prevState.state !== 'blocked') {
            return { state: 'blocked', location: nextLocation };
          }
          return prevState;
        });
      } else {
        // If already blocked but new navigation attempt, update pending location
        // This handles rapid back/forward clicks
        pendingLocationRef.current = nextLocation;
        setBlockerState(prevState => {
          if (prevState.state === 'blocked') {
            return { state: 'blocked', location: nextLocation };
          }
          return prevState;
        });
      }

      return false; // Block navigation
    });

    unblockRef.current = unblock;

    return () => {
      if (unblock) {
        try {
          unblock();
        } catch (e) {
          // Ignore cleanup errors
        }
      }
    };
  }, [history, blockerState.state]); // Include blockerState.state to re-register after reset

  const blocker = useMemo(() => {
    return {
      state: blockerState.state,
      location: blockerState.location,
      proceed: () => {
        // Prevent concurrent calls
        if (isProcessingRef.current) return;
        
        // Use ref to check blocking state immediately
        if (isBlockedRef.current && pendingLocationRef.current && unblockRef.current) {
          isProcessingRef.current = true;
          
          const location = pendingLocationRef.current;
          const unblock = unblockRef.current;
          
          // Reset blocking state immediately
          isBlockedRef.current = false;
          unblockRef.current = null;
          pendingLocationRef.current = null;
          
          // Update state
          setBlockerState({ state: 'unblocked', location: null });
          
          // Unblock history first
          unblock();
          
          // Navigate to the pending location
          if (history && location.pathname) {
            // Use requestAnimationFrame to ensure unblock completes first
            requestAnimationFrame(() => {
              try {
                history.push(location.pathname + location.search);
                // Sync currentLocationRef after navigation
                // Use a small delay to ensure history.location is updated
                setTimeout(() => {
                  if (history?.location) {
                    currentLocationRef.current = {
                      pathname: history.location.pathname || '',
                      search: history.location.search || ''
                    };
                  }
                  isProcessingRef.current = false;
                }, 10);
              } catch (e) {
                // If navigation fails, still reset processing flag
                isProcessingRef.current = false;
              }
            });
          } else {
            isProcessingRef.current = false;
          }
        }
      },
      reset: () => {
        // Prevent concurrent calls
        if (isProcessingRef.current) return;
        
        // Use ref to check blocking state immediately
        if (isBlockedRef.current && unblockRef.current) {
          isProcessingRef.current = true;
          
          const unblock = unblockRef.current;
          
          // Reset blocking state immediately
          isBlockedRef.current = false;
          unblockRef.current = null;
          pendingLocationRef.current = null;
          
          // Update state - this will trigger the useEffect to re-register the block
          setBlockerState({ state: 'unblocked', location: null });
          
          // Unblock history
          try {
            unblock();
            // Sync currentLocationRef with actual history.location after reset
            // This ensures we're tracking the correct location after canceling navigation
            if (history?.location) {
              currentLocationRef.current = {
                pathname: history.location.pathname || '',
                search: history.location.search || ''
              };
            }
          } finally {
            // Reset processing flag
            setTimeout(() => {
              isProcessingRef.current = false;
            }, 0);
          }
        } else {
          // Even if not blocked, ensure state is unblocked
          // This handles cases where blocker state might be inconsistent
          if (blockerState.state !== 'unblocked') {
            setBlockerState({ state: 'unblocked', location: null });
          }
          isBlockedRef.current = false;
          isProcessingRef.current = false;
        }
      }
    };
  }, [blockerState, history]);

  return blocker;
}

export default useBlocker;
